/**
 * UserDropdown Component
 * Displays user avatar and dropdown menu with navigation options
 */

import { useNavigate } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "@/store/hooks";
import { logout } from "@/store/slices/authSlice";
import {
  selectUserInitials,
  selectProfilePhotoUrl,
} from "@/store/selectors/teacherProfileSelectors";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { LayoutDashboard, User, LogOut, ShieldCheck } from "lucide-react";

interface UserDropdownProps {
  userName?: string;
  userEmail?: string;
}

export function UserDropdown({ userName, userEmail }: UserDropdownProps) {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const initials = useAppSelector(selectUserInitials);
  const photoUrl = useAppSelector(selectProfilePhotoUrl);
  const user = useAppSelector((state) => state.auth.user);

  const handleLogout = async () => {
    await dispatch(logout());
    navigate("/login");
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger className="focus:outline-none">
        <div className="flex items-center gap-2 hover:opacity-80 transition-opacity cursor-pointer">
          <Avatar className="h-9 w-9 border-2 border-white/20">
            <AvatarImage src={photoUrl || undefined} alt={userName || "User"} />
            <AvatarFallback className="bg-white/10 text-white font-semibold">
              {initials}
            </AvatarFallback>
          </Avatar>
          {userName && (
            <div className="hidden md:block text-left">
              <p className="text-sm font-medium text-white">{userName}</p>
              {userEmail && (
                <p className="text-xs text-white/70">{userEmail}</p>
              )}
            </div>
          )}
        </div>
      </DropdownMenuTrigger>

      <DropdownMenuContent
        align="end"
        className="w-56 bg-card/95 backdrop-blur-sm border-border/50"
      >
        <DropdownMenuLabel>
          <div className="flex flex-col space-y-1">
            <p className="text-sm font-medium leading-none">
              {userName || "User"}
            </p>
            {userEmail && (
              <p className="text-xs leading-none text-muted-foreground">
                {userEmail}
              </p>
            )}
          </div>
        </DropdownMenuLabel>
        <DropdownMenuSeparator />

        <DropdownMenuItem
          onClick={() => navigate("/dashboard")}
          className="cursor-pointer"
        >
          <LayoutDashboard className="mr-2 h-4 w-4" />
          <span>Dashboard</span>
        </DropdownMenuItem>

        <DropdownMenuItem
          onClick={() => navigate("/profile")}
          className="cursor-pointer"
        >
          <User className="mr-2 h-4 w-4" />
          <span>Profile</span>
        </DropdownMenuItem>

        {user?.is_admin && (
          <DropdownMenuItem
            onClick={() => navigate("/admin")}
            className="cursor-pointer"
          >
            <ShieldCheck className="mr-2 h-4 w-4" />
            <span>Admin</span>
          </DropdownMenuItem>
        )}

        <DropdownMenuSeparator />

        <DropdownMenuItem
          onClick={handleLogout}
          className="cursor-pointer text-red-600 dark:text-red-400 focus:text-red-600 dark:focus:text-red-400"
        >
          <LogOut className="mr-2 h-4 w-4" />
          <span>Logout</span>
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
